import React from 'react';
import { Button } from '@progress/kendo-react-buttons';
import Markdown from './markdown';
import { DataInsightsSection } from './insights';

// Slide-in panel for the AI analysis report. Triggered by the "Run AI
// Analysis" button in the header. The drawer mounts when `open` is true
// and slides in from the right; click the backdrop or close button to
// dismiss. The orchestrator run continues in the parent regardless of
// drawer state — closing doesn't cancel the run.

export default function AiDrawer({
  open,
  onClose,
  report,
  running,
  market,
  range,
  insights,
  onRefreshInsights
}) {
  // Lock scroll on body while open so the drawer's own scroll behaves.
  React.useEffect(() => {
    if (!open) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  // Escape closes the drawer.
  React.useEffect(() => {
    if (!open) return undefined;
    const onKey = e => {
      if (e.key === 'Escape') onClose && onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);
  if (!open) return null;
  const hasReport = Boolean(report);
  const subtitle = market ? `${market}${range ? ' · ' + range : ''}` : null;
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("div", {
    className: "mi-drawer-backdrop",
    onClick: onClose,
    "aria-hidden": "true"
  }), /*#__PURE__*/React.createElement("aside", {
    className: "mi-drawer",
    role: "dialog",
    "aria-label": "AI Analysis Report"
  }, /*#__PURE__*/React.createElement("header", {
    className: "mi-drawer-header"
  }, /*#__PURE__*/React.createElement("div", {
    className: "mi-drawer-title"
  }, /*#__PURE__*/React.createElement("h2", null, "AI Analysis"), subtitle && /*#__PURE__*/React.createElement("div", {
    className: "mi-drawer-subtitle"
  }, subtitle)), /*#__PURE__*/React.createElement("div", {
    className: "mi-drawer-actions"
  }, hasReport && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Button, {
    fillMode: "flat",
    size: "small",
    onClick: () => window.print()
  }, "Export PDF"), /*#__PURE__*/React.createElement(Button, {
    fillMode: "flat",
    size: "small",
    onClick: () => {
      if (navigator.clipboard && report) {
        navigator.clipboard.writeText(report);
      }
    }
  }, "Copy")), /*#__PURE__*/React.createElement(Button, {
    fillMode: "flat",
    size: "small",
    onClick: onClose,
    "aria-label": "Close"
  }, /*#__PURE__*/React.createElement("span", {
    className: "material-icons",
    style: {
      fontSize: 20
    }
  }, "close")))), /*#__PURE__*/React.createElement("div", {
    className: "mi-drawer-body"
  }, running && /*#__PURE__*/React.createElement("div", {
    className: "mi-summary-loading"
  }, /*#__PURE__*/React.createElement("span", {
    className: "mi-spinner",
    "aria-hidden": "true"
  }), "Generating analysis\u2026 this can take up to a minute."), !running && !hasReport && /*#__PURE__*/React.createElement("div", {
    className: "mi-panel-empty"
  }, "No report available yet."), !running && hasReport && /*#__PURE__*/React.createElement(Markdown, {
    source: report,
    className: "mi-summary-content"
  }), (hasReport || insights && insights.running) && /*#__PURE__*/React.createElement(DataInsightsSection, {
    insights: insights,
    onRefresh: onRefreshInsights
  }))));
}